import axios from 'axios'
import { store } from './index'
import { initialState } from './Reducers'
import { equalsIgnoreCase, isEmpty } from '../Util'
import { initI18next } from '../i18n'
import { languages } from '../i18n-languages'

const INIT_STATE = "INIT_STATE"
const SET_OPTIONS = "GET_OPTIONS"
const SET_SETTINGS = "SET_SETTINGS"
const GET_QUOTE = "GET_QUOTE"
const SET_TRANSACTION = "SET_TRANSACTION"
const SET_AGREEMENT = "SET_AGREEMENT"
const SET_ERROR = "SET_ERROR"

const GET = 'Get'
const POST = 'Post'
const PATCH = 'Patch'

const sicProxyUrl = process.env.REACT_APP_PROXY_SIC_URL

const getTransactionId = () => store.getState().Reducer?.transactionId
const isTransactionProcessing = (status = undefined) => { 
  if (!isEmpty(status))
    return status === 'Started' || status === 'Assessing'
  return store.getState().Reducer?.isTransactionProcessing;
}
 

const initStore = async (params, callback) => {   
  try {
    await initState(params);
    let resp = await callApi(GET, '/purchaser/settings');
    store.dispatch({type: SET_SETTINGS, payload : {settings : resp.data}})
    resp = await callApi(GET, '/transactions/' + getTransactionId())
    const {shouldIdentifyBuyer, shouldSearchBuyer, buyerAdditionalInformation} = resp.data
    let purchaserBuyer;
    if (shouldIdentifyBuyer)
      purchaserBuyer = await getPurchaserBuyer()
    store.dispatch({type: SET_TRANSACTION, payload: {'backUrl': resp.data?.backUrl, 
                'IsGDPRAccepted': resp.data?.IsGDPRAccepted, 
                'isTransactionProcessing': isTransactionProcessing(resp.data.status),
                'transactionStatus': resp.data.status,
                'shouldIdentifyBuyer': shouldIdentifyBuyer,
                'shouldSearchBuyer': shouldSearchBuyer,
                'buyerAdditionalInformation': buyerAdditionalInformation,
                'purchaserBuyer': purchaserBuyer
              }})
  } catch (err) {       
    handleException(err)
  } finally {
    const language = languages.find(lng => equalsIgnoreCase(lng.code, store.getState().Reducer.settings?.languageCode))
    const languageCode = isEmpty(language) ? 'en' : language.code
    initI18next(callback, languageCode)
  }
}

const initState = (params) => {
  return new Promise((resolve) => {
    const token = params.get("token");
    if (!isEmpty(token)) {
      const transactionId = params.get("transactionId")
      store.dispatch({ type: INIT_STATE, payload : { ...initialState, token, transactionId }});
    }
    return resolve();
  })
}

const getStrongVerificationOptions = async () => {
  try {
    let resp = await callApi(GET, '/purchaser/strongverification/options')
    store.dispatch({type: SET_OPTIONS, payload: { options : resp.data }})
  } catch (err) {
    handleException(err)
  }
}

const getQuote = async (sessionToken, setState) => {
    try {
      if (!isEmpty(getTransactionId())) {
        const resp = await callApi(POST, '/coverage/transactions',{transactionId : getTransactionId(), sessionToken})
        refreshTransactionStatus()
        const {requirement, callbackUrl, status} = resp.data
        setState(requirement, callbackUrl, status)
      }
    } catch (err) {
      handleException(err)
    }
}

const fetchCoverstatus = async(setState) => {
  try {
    const resp = await callApi(GET, '/transactions/'+ getTransactionId() + '/coverage')
    refreshTransactionStatus()
    if (typeof setState === 'function'){
      const {requirement, callbackUrl, status} = resp.data
      setState(requirement, callbackUrl, status)
    } else 
      return resp.data
  } catch (err) {
    handleException(err)
  } 
}

const refreshTransactionStatus = async () => {
  try {
    const resp = await callApi(GET, '/transactions/'+ getTransactionId() + '/status', null, false)
    store.dispatch({type: SET_TRANSACTION, payload: {'isTransactionProcessing': isTransactionProcessing(resp.data.status)}})
  } catch (err) {
    handleException(err)
  }
} 

const verifyTransaction = async (callback) => {
  try {
    await callApi(POST, '/transactions/'+ getTransactionId() + '/verify')
    callback()
  } catch (err) {
    handleException(err)
  }
} 

const setTransactionAgreement = async (callback) => {
  try {
    const IsGDPRAccepted = store.getState().Reducer?.IsGDPRAccepted
    await callApi(PATCH, '/transactions/'+ getTransactionId() +'/agreement', {"isAccepted" : !IsGDPRAccepted})
    store.dispatch({type: SET_AGREEMENT, payload: { IsGDPRAccepted : !IsGDPRAccepted}})
  } catch (err) {
    handleException(err)
  } finally {
    callback();
  }
}

const cancelTransaction = async (event) => {
  try {
    if (!isEmpty(getTransactionId()) && isTransactionProcessing()) {
        await callApi(PATCH, '/transactions/'+ getTransactionId() +'/cancel', null, false)
    }
  } catch (err) {
    handleException(err)
  } 
}

const getPurchaserBuyer = async () => {
  try {
    return (await callApi(GET, '/transactions/'+ getTransactionId() + '/purchaser/buyer')).data
  } catch (err) {
    handleException()
  }
}

const searchCompanies = async (formData, callback, setIsTvaValidationError) => {

  try {
    const result = (await callApi(POST, '/transactions/'+getTransactionId()+'/company/search', formData)).data;
    callback(result)
  } catch (err) {
    if (err.response?.status === 422) 
      setIsTvaValidationError()
    else 
      handleException(err)
  }
}

const patchTransaction = async (buyer) => {
  try {
    await callApi(PATCH, '/transactions/'+getTransactionId(), buyer)
  } catch (err) {
    handleException(err)
  }
}

const callApi = (method, service, data = null, shouldCheckTransaction = true) => {
  if (shouldCheckTransaction && !isTransactionProcessing())
    return Promise.reject(new Error('403', 'operation not allowed'))
  return axios({
    method: method,
    url : sicProxyUrl + service,
    headers : { 'token' : store.getState().Reducer?.token },
    data : data
  })
}

const handleException = (err) => {
  if (err?.response?.status === 401)
    store.dispatch({type: SET_TRANSACTION, payload: {'isTransactionProcessing': false}})
  store.dispatch({type: SET_ERROR, payload: { error : 'A technical problem does not allow access to the verification service.'}})
}


export {initStore, getStrongVerificationOptions, getQuote, fetchCoverstatus, verifyTransaction, setTransactionAgreement, cancelTransaction, isTransactionProcessing, searchCompanies, patchTransaction, handleException, callApi,
  SET_SETTINGS, INIT_STATE, SET_OPTIONS, GET_QUOTE, SET_TRANSACTION, SET_AGREEMENT, SET_ERROR, GET, POST, PATCH}